/**
 * PlaceContextModule — "Gör mer av det här"
 * Auto-generated contextual module under Sammanhang.
 * Rule B: Only renders if total (events + nearby places) >= 2.
 *
 * Uses single RPC: get_place_context.
 * Read-only. No mutations.
 */
import { Link } from "react-router-dom";
import { Calendar, MapPin, ChevronRight } from "lucide-react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import { usePlaceContext, type ContextEvent, type ContextPlace } from "@/hooks/usePlaceContext";
import { activityDetailPath } from "@/lib/paths";
import { track } from "@/lib/analytics";
import { useTrackOnce } from "@/lib/analytics/useTrackOnce";
import { Badge } from "@/components/ui/badge";

interface Props {
  placeId: string;
}

const MIN_ITEMS = 2;

/** Resolve a public-media asset URL from bucket + path */
function resolvePublicUrl(bucket: string | null, path: string | null): string | null {
  if (!bucket || !path || bucket !== "public-media") return null;
  const { data } = supabase.storage.from(bucket).getPublicUrl(path);
  return data.publicUrl;
}

function ContextEventCard({ event, sourcePlaceId }: { event: ContextEvent; sourcePlaceId: string }) {
  const dateStr = event.start_date
    ? format(new Date(event.start_date), "d MMM", { locale: sv })
    : null;
  const linkTo = activityDetailPath(event.id);
  const imageUrl = resolvePublicUrl(event.hero_image_bucket, event.hero_image_path_640);

  const handleClick = () => {
    track("place_context_item_clicked", {
      source_place_id: sourcePlaceId,
      target_type: "event",
      target_id: event.id,
    });
  };

  return (
    <Link to={linkTo} onClick={handleClick} className="group block rounded-xl border border-border/40 bg-card overflow-hidden transition-colors hover:border-primary/20 hover:shadow-sm">
      <div className="flex gap-3 p-3">
        <div className="shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-muted">
          {imageUrl ? (
            <img src={imageUrl} alt={event.title} loading="lazy" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Calendar className="h-5 w-5 text-muted-foreground/40" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0 flex flex-col justify-center">
          <div className="flex items-center gap-1.5 mb-0.5">
            <Badge variant="secondary" className="font-normal text-[10px] px-1.5 py-0">
              Aktivitet
            </Badge>
          </div>
          <p className="font-medium text-sm text-foreground line-clamp-1 group-hover:text-primary transition-colors">
            {event.title}
          </p>
          {dateStr && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
              <Calendar className="h-3 w-3" />
              {dateStr}
            </span>
          )}
        </div>
        <ChevronRight className="h-4 w-4 text-muted-foreground/40 group-hover:text-primary transition-colors shrink-0 self-center" />
      </div>
    </Link>
  );
}

function ContextPlaceCard({ place, sourcePlaceId }: { place: ContextPlace; sourcePlaceId: string }) {
  const imageUrl = resolvePublicUrl(place.hero_image_bucket, place.hero_image_path_640);

  const handleClick = () => {
    track("place_context_item_clicked", {
      source_place_id: sourcePlaceId,
      target_type: "place",
      target_id: place.id,
    });
  };

  return (
    <Link to={`/place/${place.slug}`} onClick={handleClick} className="group block rounded-xl border border-border/40 bg-card overflow-hidden transition-colors hover:border-primary/20 hover:shadow-sm">
      <div className="flex gap-3 p-3">
        <div className="shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-muted">
          {imageUrl ? (
            <img src={imageUrl} alt={place.title} loading="lazy" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <MapPin className="h-5 w-5 text-muted-foreground/40" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0 flex flex-col justify-center">
          <p className="font-medium text-sm text-foreground line-clamp-1 group-hover:text-primary transition-colors">
            {place.title}
          </p>
          <span className="text-xs text-muted-foreground">
            {place.distance_km} km härifrån
          </span>
        </div>
        <ChevronRight className="h-4 w-4 text-muted-foreground/40 group-hover:text-primary transition-colors shrink-0 self-center" />
      </div>
    </Link>
  );
}

export function PlaceContextModule({ placeId }: Props) {
  const { events, places, isLoading } = usePlaceContext(placeId);

  const totalItems = events.length + places.length;
  const shouldRender = !isLoading && totalItems >= MIN_ITEMS;

  // Analytics: track render (deduplicated, strict-mode safe)
  useTrackOnce(
    "place_context_rendered",
    {
      place_id: placeId,
      events_count: events.length,
      places_count: places.length,
    },
    shouldRender ? `ctx-${placeId}` : null,
  );

  if (!shouldRender) return null;

  return (
    <section>
      <h2 className="font-display text-lg text-foreground mb-1">
        Gör mer av det här
      </h2>
      <p className="text-sm text-muted-foreground mb-4">
        När du ändå är här…
      </p>

      <div className="space-y-2">
        {events.map((event) => (
          <ContextEventCard key={event.id} event={event} sourcePlaceId={placeId} />
        ))}
        {places.map((place) => (
          <ContextPlaceCard key={place.id} place={place} sourcePlaceId={placeId} />
        ))}
      </div>
    </section>
  );
}
